'use client';

import { useRef } from 'react';

import type { Place, TypesenseEventDocument } from '@/types';
import { Button } from '@/ui';
import { EmptyState } from '@/ui/block/empty-state';
import { IconCalendarEvent, IconChevronDown, IconLoader2 } from '@tabler/icons-react';
import { format } from 'date-fns';

import { useEvents } from '@/lib/hooks/use-events';

import { Text } from '../text';
import { EventCard, EventCardSkeleton } from './event-card';

export function EventSearchCalendar({
  placesData,
  eventsData: { status, events, showMore, isLastPage },
}: {
  placesData: Place[] | undefined;
  eventsData: ReturnType<typeof useEvents>;
}) {
  const top = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className="absolute" ref={top} />

      <div className="divide-border/50 relative grid w-full max-w-screen-2xl grow auto-cols-fr grid-cols-1 divide-x sm:grid-cols-2 sm:divide-y md:grid-cols-3 xl:grid-cols-4">
        {!status || (status === 'loading' && !events?.length) ? (
          Array.from({ length: 10 }).map((_, i) => <EventCardSkeleton key={i} />)
        ) : !events?.length && status === 'idle' ? (
          <EmptyState contentName="events" icon={IconCalendarEvent} />
        ) : (
          events
            ?.reduce((acc: { day: string; events: TypesenseEventDocument[] }[], event) => {
              // Get start date from the event
              const date = new Date(event.dates * 1000);
              const day = format(date, 'EEE do MMM yyyy');

              const existingGroup = acc.find((group) => group.day === day);
              if (existingGroup) {
                existingGroup.events.push(event);
              } else {
                acc.push({ day, events: [event] });
              }

              return acc;
            }, [])
            .map(({ day, events }) => (
              <div
                className="flex flex-col gap-4 p-4 first:pt-0 sm:p-8 sm:nth-[2]:pt-0 md:nth-[3]:pt-0 xl:nth-[4]:pt-0"
                key={day}
              >
                <Text element="p" className="text-primary! m-0! font-medium">
                  {day}
                </Text>

                {events.map(({ image, ...event }) => (
                  <EventCard
                    key={event.id}
                    size="lg"
                    place={placesData?.find((p) => event._referencedBy.includes(p.id))}
                    image={image}
                    {...event}
                  />
                ))}
              </div>
            ))
        )}

        <div className="col-span-full flex p-4 sm:justify-center sm:p-8">
          <Button disabled={status === 'loading' || isLastPage} onClick={() => showMore()}>
            Load More
            {status === 'loading' ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconChevronDown className="size-4" />
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
